import { useStore } from 'hooks/useStore';
import { User } from 'Interfaces/BDPortalInterfaces';
import { paralleIterationsResponseInterface } from 'Interfaces/BDPortalInterfaces/AddNewIterationInterfaces';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';

const useParallelIterationsTable = (
  conceptName: string,
  gotToIterationCreateNewIterationCallBack: () => void
) => {
  const {
    BDPortalStore: { dropdownDetails },
  } = useStore();
  const [parallelIterationsData, setParallelIterationsData] =
    useState<paralleIterationsResponseInterface>([]);
  const [isParallelIterationDataLoading, setIsParallelIterationDataLoading] = useState(false);
  const navigate = useNavigate();

  const handleFetchParallelIterationsData = useCallback(async () => {
    try {
      setIsParallelIterationDataLoading(true);
      const data = await bdportalScreenServices.getParallelIterations({
        moleculeAndForm: conceptName,
      });
      setParallelIterationsData(data);
    } catch {
      toast.error('Failed to fetch parallel iterations');
    } finally {
      setIsParallelIterationDataLoading(false);
    }
  }, [conceptName]);

  const handleGoToIteration = (index: number) => {
    const iteration_id = parallelIterationsData[index].iteration.OpportunityIterationsID;
    const opportunity_id = parallelIterationsData[index].iteration.OpportunityID;
    navigate(
      `/bdportal/optracker/board?opportunityIdParam=${opportunity_id}&opportunityIterationIdParam=${iteration_id}`
    );
    gotToIterationCreateNewIterationCallBack();
  };

  const handleGoToCreateNewIteration = (index: number) => {
    const iteration_id = parallelIterationsData[index].iteration.OpportunityIterationsID;
    const opportunity_id = parallelIterationsData[index].iteration.OpportunityID;
    navigate(
      `/bdportal/optracker/board?opportunityIdParam=${opportunity_id}&opportunityIterationIdParam=${iteration_id}&isIterationOpen=${true}`
    );
    gotToIterationCreateNewIterationCallBack();
  };

  const getColumnValue = (
    iterationIndex: number,
    columnType: 'source' | 'stage' | 'status' | 'analyst' | 'strength' | 'primaryPartner'
  ) => {
    if (columnType === 'source') {
      if (parallelIterationsData[iterationIndex].iteration.IterationType === 'NC') {
        let source_name = '-';
        dropdownDetails?.NC.Source.forEach(source => {
          if (source.ID === parallelIterationsData[iterationIndex].iteration_data.SourceID) {
            source_name = source.Source;
          }
        });
        return source_name;
      } else {
        let source_name = '-';
        dropdownDetails?.MAS.Source.forEach(source => {
          if (source.ID === parallelIterationsData[iterationIndex].iteration_data.SourceID) {
            source_name = source.Source;
          }
        });
        return source_name;
      }
    } else if (columnType === 'stage') {
      if (parallelIterationsData[iterationIndex].iteration.IterationType === 'NC') {
        let stage_name = '-';
        dropdownDetails?.NC.Stage.forEach(stage => {
          if (stage.ID === parallelIterationsData[iterationIndex].iteration_data.StageID) {
            stage_name = stage.Stage;
          }
        });
        return stage_name;
      } else {
        let stage_name = '-';
        dropdownDetails?.MAS.Stage.forEach(stage => {
          if (stage.ID === parallelIterationsData[iterationIndex].iteration_data.StageID) {
            stage_name = stage.Stage;
          }
        });
        return stage_name;
      }
    } else if (columnType === 'status') {
      if (parallelIterationsData[iterationIndex].iteration.IterationType === 'NC') {
        let status_name = '-';
        dropdownDetails?.NC.Status.forEach(status => {
          if (status.ID === parallelIterationsData[iterationIndex].iteration_data.StatusID) {
            status_name = status.Status;
          }
        });
        return status_name;
      } else {
        let status_name = '-';
        dropdownDetails?.MAS.Status.forEach(status => {
          if (status.ID === parallelIterationsData[iterationIndex].iteration_data.StatusID) {
            status_name = status.Status;
          }
        });
        return status_name;
      }
    } else if (columnType === 'strength') {
      // Strength column value is handled separately via getConceptsData
      return '-';
    } else if (columnType === 'primaryPartner') {
      return parallelIterationsData[iterationIndex].iteration_data.PrimarySupplierID
        ? parallelIterationsData[iterationIndex].iteration_data.PrimarySupplierID.length > 17
          ? parallelIterationsData[iterationIndex].iteration_data.PrimarySupplierID.substring(
              0,
              8
            ) + '...'
          : parallelIterationsData[iterationIndex].iteration_data.PrimarySupplierID
        : '-';
    }
    return '-';
  };

  const getAnalyst = (iterationIndex: number) => {
    if (parallelIterationsData[iterationIndex].iteration.IterationType === 'NC') {
      let analyst: User | null = null;
      dropdownDetails?.NC.User?.forEach(user => {
        if (user.ID === parallelIterationsData[iterationIndex].iteration_data.AnalystID) {
          analyst = user;
        }
      });
      return analyst;
    } else {
      let analyst: User | null = null;
      dropdownDetails?.MAS.User?.forEach(user => {
        if (user.ID === parallelIterationsData[iterationIndex].iteration_data.AnalystID) {
          analyst = user;
        }
      });
      return analyst;
    }
  };

  const getConceptsData = (rowIndex: number) => {
    if (parallelIterationsData[rowIndex].conceptData) {
      let result = '';
      const strength_set: Set<string> = new Set();
      parallelIterationsData[rowIndex].conceptData.forEach((conceptInformation, index) => {
        if (!strength_set.has(conceptInformation.Strength)) {
          if (index !== 0) {
            result += ', ';
          }
          result += conceptInformation.Strength;
        }
        strength_set.add(conceptInformation.Strength);
      });
      return {
        shortenedConceptData: result ? result.substring(0, 10) + '...' : '',
        fullConceptData: result,
      };
    }
    return {
      shortenedConceptData: '',
      fullConceptData: '',
    };
  };

  useEffect(() => {
    handleFetchParallelIterationsData();
  }, [conceptName, handleFetchParallelIterationsData]);

  return {
    parallelIterationsData,
    handleGoToIteration,
    handleGoToCreateNewIteration,
    getColumnValue,
    isParallelIterationDataLoading,
    getAnalyst,
    getConceptsData,
  };
};

export default useParallelIterationsTable;
